import { defineCustomElement, getCurrentInstance, createApp } from "vue";
import { createPinia, setActivePinia } from "pinia";
import App from "./App.vue";
import Cats from "./Categories.vue";

import TripPlannerToggle from "./components/OperatorTemplateTripPlannerButton.vue";
import "./styles/theme.css";
import { pluginRegistry } from "./plugins/registry.js";
import { configService } from "./services/config.js";
import { componentRegistry } from "./services/componentRegistry.js";
import { useConfigStore } from "./stores/config.js";

// --- Bootstrap ---
// 1. Resolve config from window.VWC_CONFIG (or defaults)
const config = configService.resolve();

// 2. Global Pinia instance
const globalPinia = createPinia();
setActivePinia(globalPinia);

// 3. Initialise config store before any element mounts
useConfigStore().init(config);

// 4. Register client-specific component overrides
componentRegistry.setup(config);

// 5. Inject client theme CSS
if (config.client === "vaw") {
  import("./styles/vaw.css");
}
if (config.client === "veg") {
  import("./styles/veg.css");
}

if (config.client === "bundabergregion.org") {
  import("./styles/bundy-theme.js");
}

// Pinia plugin for custom elements
const piniaPlugin = {
  install(app) {
    app.config.globalProperties.$pinia = globalPinia;
    app.provide("pinia", globalPinia);
    setActivePinia(globalPinia);
  },
};

// Enhanced custom element wrapper with plugin support and Pinia
function createEnhancedCustomElement(component, elementName) {
  // Create a wrapper component that ensures Pinia is available
  const WrappedComponent = {
    ...component,
    setup(props, ctx) {
      // Ensure Pinia is active
      setActivePinia(globalPinia);

      // Call original setup if it exists
      if (component.setup) {
        return component.setup(props, ctx);
      }
    },
    beforeCreate() {
      // Set Pinia as active before any component lifecycle
      setActivePinia(globalPinia);

      if (component.beforeCreate) {
        component.beforeCreate.call(this);
      }
    },
    created() {
      // Ensure Pinia is still active
      setActivePinia(globalPinia);

      if (component.created) {
        component.created.call(this);
      }
    },
  };

  // Define the custom element with Pinia plugin
  const CustomElement = defineCustomElement(WrappedComponent, {
    plugins: [piniaPlugin],
  });

  return class EnhancedCustomElement extends CustomElement {
    constructor() {
      super();
      // Ensure Pinia is active immediately
      setActivePinia(globalPinia);
    }

    connectedCallback() {
      // Set Pinia as active when element connects
      setActivePinia(globalPinia);
      super.connectedCallback();
      this.loadPlugins();
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      this.unloadPlugins();
    }

    async loadPlugins() {
      try {
        const attributes = this.getAttributes();

        await pluginRegistry.loadConditional({
          attributes,
          features: this.getFeatureFlags(),
          element: elementName,
        });

        this.logPluginStatus();
      } catch (error) {
        console.error(`Failed to load plugins for ${elementName}:`, error);
      }
    }

    async unloadPlugins() {
      if (__DEV__) {
        console.log(`Cleaning up plugins for ${elementName}`);
      }
    }

    getAttributes() {
      const attributes = {};
      for (const attr of this.attributes) {
        attributes[attr.name] = attr.value;
      }
      return attributes;
    }

    getFeatureFlags() {
      const flagMap = {
        analytics: "analytics",
        "track-events": "tracking",
        "performance-monitoring": "performance",
      };

      return Object.entries(flagMap)
        .filter(([attr]) => this.hasAttribute(attr))
        .map(([, flag]) => flag);
    }

    logPluginStatus() {
      if (__DEV__) {
        console.log(`Plugins loaded for ${elementName}:`, pluginRegistry.getLoadedPlugins());
      }
    }
  };
}

// Component configurations
const components = [
  { component: App, name: "my-app" },
  { component: Cats, name: "cat-app" },
  { component: TripPlannerToggle, name: "trip-planner-toggle" },
];

// Register all custom elements
components.forEach(({ component, name }) => {
  if (!customElements.get(name)) {
    const CustomElement = createEnhancedCustomElement(component, name);
    customElements.define(name, CustomElement);
  }
});

// Global exports for external access
window.VueWebComponentsPluginRegistry = pluginRegistry;
window.VueWebComponentsPinia = globalPinia;

// Development utilities
if (__DEV__) {
  console.log("Vue Web Components initialized with plugin system and Pinia");
  console.log("Available plugins:", Array.from(pluginRegistry.plugins.keys()));

  window.VWC_DEBUG = {
    pluginRegistry,
    pinia: globalPinia,
    getStats: () => ({
      plugins: pluginRegistry.getStats(),
      stores: Object.keys(globalPinia.state.value),
      performance: window.VWC_PERFORMANCE?.getMetrics?.() || "Performance monitoring not available",
    }),
  };
}
